import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { IProcedure, IProcedurePage } from 'src/app/models/procedure.model';
import { ProcedureService } from 'src/app/services/procedure.service';
import { SearchService } from '../../services/search.service';
import { 
  faAngleDoubleLeft, 
  faAngleDoubleRight, 
  faAngleLeft, 
  faAngleRight, 
  faSort, 
  faSortDown, 
  faSortUp, 
  faTimes 
} from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-procedures-v2',
  templateUrl: './procedures.component.html',
  styleUrls: ['./procedures.component.sass']
})
export class ProceduresV2Component implements OnInit, OnDestroy {
  loading = false;
  pageSize = 10;
  currentPage = 0;
  totalPages = 1;
  totalElements = 0;
  sortBy = 'ASC';
  orderBy = 'description';
  searchTerm: string | undefined = undefined;
  procedures: IProcedure[] = [];
  private searchSubscription?: Subscription;

  // Delete modal
  showConfirmDeletePopup = false;
  procedureToDelete = '';

  // Expor Math para o template
  Math = Math;

  pageSizeOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
    { value: 1000, label: 'Todos' }
  ];

  // Icons
  faSort = faSort;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faAngleDoubleLeft = faAngleDoubleLeft;
  faAngleLeft = faAngleLeft;
  faAngleRight = faAngleRight;
  faAngleDoubleRight = faAngleDoubleRight;
  faTimes = faTimes;

  constructor(
    private procedureService: ProcedureService,
    private router: Router,
    private searchService: SearchService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    
    // Limpa o termo de busca ao entrar na página
    this.searchService.setSearchTerm('');
    
    // Carrega a lista inicial
    this.getProcedures(0, this.pageSize);

    // Escuta mudanças no termo de pesquisa do header
    this.searchSubscription = this.searchService.searchTerm$.subscribe((term: string) => {
      this.searchTerm = term || undefined;
      this.getProcedures(0, this.pageSize);
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  toggleSort(column: string): void {
    if (this.orderBy === column) {
      this.sortBy = this.sortBy === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.orderBy = column;
      this.sortBy = 'ASC';
    }
    this.getProcedures(0, this.pageSize);
  }

  getProcedures(pageNumber: number, pageSize: number): void {
    this.loading = true;
    this.currentPage = pageNumber;
    this.procedureService.listProcedures(pageNumber, pageSize, this.orderBy, this.sortBy, this.searchTerm || '')
      .subscribe({
        next: (proceduresPaged: IProcedurePage) => {
          this.procedures = proceduresPaged.data;
          if (proceduresPaged.page_info) {
            this.currentPage = proceduresPaged.page_info.current_page;
            this.totalPages = proceduresPaged.page_info.total_pages;
            this.totalElements = proceduresPaged.page_info.total_elements;
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Erro ao buscar procedimentos:', error);
          this.loading = false;
        }
      });
  }

  goToFirstPage(): void {
    if (this.currentPage > 0) {
      this.getProcedures(0, this.pageSize);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.getProcedures(this.currentPage - 1, this.pageSize);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.getProcedures(this.currentPage + 1, this.pageSize);
    }
  }

  goToLastPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.getProcedures(this.totalPages - 1, this.pageSize);
    }
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.getProcedures(0, this.pageSize);
  }

  generatePagination(current: number, total: number): (string | number)[] {
    const range = (start: number, end: number): number[] => {
      return Array.from({ length: (end - start) }, (v, k) => k + start);
    };
    let pages: (string | number)[] = [1];
    let left = current - 1;
    let right = current + 1;
    if (left < 2) right += 2 - left;
    if (right > total - 1) left -= right - (total - 1);
    left = Math.max(2, left);
    right = Math.min(total - 1, right);
    if (left > 2) pages.push('...');
    pages = pages.concat(range(left, right + 1));
    if (right < total - 1) pages.push('...');
    if (total > 1 && total !== pages[pages.length - 1]) pages.push(total);
    return pages;
  }

  navigateTo(page: number | string): void {
    if (page !== '...') {
      this.getProcedures(+page - 1, this.pageSize);
    }
  }

  edit(id?: string): void {
    this.router.navigateByUrl('/main/v2', { skipLocationChange: true }).then(() => {
      this.router.navigate(['main', 'v2', 'procedures', id, 'edit']);
    });
  }

  showDeleteModal(id?: string): void {
    this.showConfirmDeletePopup = true;
    this.procedureToDelete = id || '';
  }

  closeDeleteModal(): void {
    this.showConfirmDeletePopup = false;
    this.procedureToDelete = '';
  }

  onDelete(): void {
    if (!this.procedureToDelete) return;

    this.loading = true;
    this.showConfirmDeletePopup = false;

    this.procedureService.deleteProcedure(this.procedureToDelete).subscribe({
      next: () => {
        this.procedureToDelete = '';
        this.getProcedures(this.currentPage, this.pageSize);
      },
      error: (error) => {
        console.error('Erro ao deletar procedimento:', error);
        this.loading = false;
        this.procedureToDelete = '';
      }
    });
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }
}
